import React, { useEffect, useId, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ListBullets, MicrosoftExcelLogo, X } from '@phosphor-icons/react';
import {
    collectAllSystemModules,
    collectSelectedModules,
    formatModuleProductionKwh,
    summarizeAllSystemModules,
    summarizeSelectedModules,
    type SelectedModuleRow,
    type SystemModuleRow,
} from '../lib/preContractSelectedModules';
import { formatPpaRate } from '../lib/preContractPpwPricing';
import {
    fetchPpwDetails,
    quoteModulePartnerPpw,
    type PpwDetailsDataset,
} from '../lib/preContractPpwDetails';
import {
    formatSandboxEscalator,
    resolvePreContractEscalator,
} from '../lib/preContractSandboxPricing';
import {
    buildModuleProductionFallback,
    type ModuleProductionFallback,
} from '../lib/preContractModuleLayout';
import { resolveStoreOpportunityId } from '../lib/preContractTitle';
import { useOneButtonStore, type OneButtonState } from '../../store/oneButtonStore';
import { useViewerStore } from '../../store/store';
import { normalizeLossesVendor, type ActiveLossesVendor } from '../../Constants';
import * as XLSX from 'xlsx';
import {
    lossesVendorProductionRatio,
    resolveActiveLossesVendor,
} from '../lib/preContractPartnerLosses';
import type { PreContractIntakeForm, PreContractRoofModuleLayout } from '../lib/preContractTypes';

type ModulesModalTab = 'selected' | 'all';

const ALL_MODULES_OPTIONS = {
    includeGreyPanels: true,
    includePurplePanels: true,
} as const;

/** Partner PPW columns stay in the file so they can be restored without rewriting the table. */
const SHOW_MODULE_PARTNER_PPW_COLUMNS = true;

export type PreContractSelectedModulesPopoverProps = {
    layouts: PreContractRoofModuleLayout[];
    /** Full max-fill catalog (grey/purple included). Falls back to session baseline. */
    allModulesLayouts?: PreContractRoofModuleLayout[];
    roofSelections?: boolean[];
    /** @deprecated List is sorted by production; fill order is ignored. */
    fillOrderIds?: string[];
    /** Annual usage target (kWh) for highlight when running total crosses it. */
    targetKwh?: number;
    /** Escalator fraction from Utility details (e.g. 0.0299). */
    escalator?: number | string | null;
    /** Target / min PPA rate ($/kWh) from Utility details. */
    rate?: number | string | null;
    /** Intake form — used to look up LightReach / Enfin PPW per module multiplier. */
    form?: PreContractIntakeForm | null;
    /** System PPW from design details ($/W). Shown in the summary; used if vendor PPWs are missing. */
    systemPpw?: number | null;
    /** oneDRAW finance PPW for running EPC (PPW × module watts). */
    oneDrawSystemPpw?: number | null;
    /** EnFin finance PPW for running EPC (PPW × module watts). */
    enfinSystemPpw?: number | null;
    /** Simulated kWh actual — used when module rows predate live production sync. */
    actualProductionKwh?: number;
    /**
     * Unused for display: the table now shows oneDRAW and EnFin kWh as separate columns
     * from the live source vendor. Kept so existing callers can still pass a ratio.
     */
    productionScale?: number;
    /**
     * Value modules the viewer has not solved yet with the same fallback the annual total
     * uses, so the list reconciles to the production figure shown next to it.
     */
    estimateMissingProduction?: boolean;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Optional label for the design context (AI card title). */
    contextLabel?: string;
    /** `toolbar` = compact ghost button; `card` = outline chip on design cards. */
    variant?: 'toolbar' | 'card';
    /** Shorter label for narrow toolbars (count + mult only). */
    compact?: boolean;
    disabled?: boolean;
};

function productionForVendor(
    sourceKwh: number | null | undefined,
    sourceVendor: ActiveLossesVendor,
    targetVendor: ActiveLossesVendor,
): number | null {
    if (sourceKwh == null || !Number.isFinite(sourceKwh) || sourceKwh <= 0) return null;
    const scaled = sourceKwh * lossesVendorProductionRatio(sourceVendor, targetVendor);
    return Number.isFinite(scaled) && scaled > 0 ? scaled : null;
}

function formatVendorProductionKwh(
    value: number | null | undefined,
    estimated?: boolean,
): string {
    const label = formatModuleProductionKwh(value);
    if (label === '—' || !estimated) return label;
    return `~${label}`;
}

/** A module row plus whether its production came from the fallback rather than the viewer. */
type PricedModuleRow = SelectedModuleRow & { estimated?: boolean };

function fillModuleRows<T extends SelectedModuleRow>(
    rows: T[],
    fallback: ModuleProductionFallback | null,
): (T & { estimated?: boolean })[] {
    if (!fallback || fallback.basis === 'none') return rows;
    const filled = rows.map((row) => {
        if (row.productionKwh != null) return row;
        const estimate = fallback.valueFor(row.solarAccess ?? 0);
        if (estimate == null || !(estimate > 0)) return row;
        return { ...row, productionKwh: estimate, estimated: true };
    });
    return filled.sort((a, b) => (b.productionKwh ?? -1) - (a.productionKwh ?? -1));
}

function sumRowProduction(rows: SelectedModuleRow[]): number | null {
    let total = 0;
    let counted = 0;
    for (const row of rows) {
        if (row.productionKwh == null) continue;
        total += row.productionKwh;
        counted += 1;
    }
    return counted > 0 ? Math.round(total) : null;
}

function formatSummaryRate(raw: number | string | null | undefined): string {
    if (raw == null || raw === '') return '—';
    const n = typeof raw === 'number' ? raw : Number(String(raw).replace(/[$,\s]/g, ''));
    return Number.isFinite(n) && n > 0 ? formatPpaRate(n) : '—';
}

function parsePpaRateValue(raw: number | string | null | undefined): number {
    if (raw == null || raw === '') return 0;
    const n = typeof raw === 'number' ? raw : Number(String(raw).replace(/[$,\s]/g, ''));
    return Number.isFinite(n) && n > 0 ? n : 0;
}

function formatModulePpw(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value)) return '—';
    return `$${value.toFixed(2)}`;
}

function formatRunningModulePrice(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value) || value <= 0) return '—';
    return value.toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
    });
}

function formatModuleWattage(wattage: number | null | undefined): string {
    if (wattage == null || !Number.isFinite(wattage) || wattage <= 0) return '—';
    return `${Math.round(wattage)} W`;
}

function ModuleIdentityCell({
    id,
    roofLabel,
    wattage,
}: {
    id: string;
    roofLabel: string;
    wattage: number | null;
}) {
    return (
        <td className="pci-selected-modules-modal__identity">
            <span className="pci-selected-modules-modal__identity-id">{id}</span>
            <span className="pci-selected-modules-modal__identity-meta">
                <span>{roofLabel || '—'}</span>
                <span className="pci-selected-modules-modal__identity-sep" aria-hidden>
                    ·
                </span>
                <span>{formatModuleWattage(wattage)}</span>
            </span>
        </td>
    );
}

function formatSystemPpw(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value) || value <= 0) return '—';
    return `$${value.toFixed(2)}/W`;
}

function moduleWattagePrice(
    modulePpw: number | null | undefined,
    wattage: number | null | undefined,
): number | null {
    if (modulePpw == null || !(modulePpw > 0) || wattage == null || !(wattage > 0)) {
        return null;
    }
    return modulePpw * wattage;
}

type ModulePartnerPpw = {
    lightReach: number | null;
    enfin: number | null;
};

function partnerPpwForMultiplier(
    multiplier: number | null | undefined,
    lookup: Map<string, ModulePartnerPpw>,
): ModulePartnerPpw {
    if (multiplier == null || !(multiplier > 0)) return { lightReach: null, enfin: null };
    return lookup.get(multiplier.toFixed(3)) ?? { lightReach: null, enfin: null };
}

function PartnerPpwCells({
    multiplier,
    lookup,
}: {
    multiplier: number | null | undefined;
    lookup: Map<string, ModulePartnerPpw>;
}) {
    const ppw = partnerPpwForMultiplier(multiplier, lookup);
    return (
        <>
            <td
                className="pci-selected-modules-modal__ppw pci-selected-modules-modal__ppw--lightreach"
                title={ppw.lightReach != null ? `LightReach PPW $${ppw.lightReach.toFixed(2)}` : undefined}
            >
                {formatModulePpw(ppw.lightReach)}
            </td>
            <td
                className="pci-selected-modules-modal__ppw pci-selected-modules-modal__ppw--enfin"
                title={ppw.enfin != null ? `Enfin PPW $${ppw.enfin.toFixed(2)}` : undefined}
            >
                {formatModulePpw(ppw.enfin)}
            </td>
        </>
    );
}

function PartnerPpwHeaders() {
    return (
        <>
            <th scope="col" className="pci-selected-modules-modal__ppw-head">
                LightReach
            </th>
            <th scope="col" className="pci-selected-modules-modal__ppw-head">
                Enfin
            </th>
        </>
    );
}

function VendorProductionHeaders() {
    return (
        <>
            <th
                scope="col"
                className="pci-selected-modules-modal__prod-head"
                aria-label="Production (oneDRAW)"
            >
                <span className="pci-selected-modules-modal__prod-head-label">Production</span>
                <span className="pci-selected-modules-modal__prod-head-vendor">(oneDRAW)</span>
            </th>
            <th
                scope="col"
                className="pci-selected-modules-modal__prod-head"
                aria-label="Running total (oneDRAW)"
            >
                <span className="pci-selected-modules-modal__prod-head-label">Running total</span>
                <span className="pci-selected-modules-modal__prod-head-vendor">(oneDRAW)</span>
            </th>
            <th
                scope="col"
                className="pci-selected-modules-modal__prod-head"
                aria-label="Running module price (oneDRAW)"
            >
                <span className="pci-selected-modules-modal__prod-head-label">Running price</span>
                <span className="pci-selected-modules-modal__prod-head-vendor">(oneDRAW)</span>
            </th>
            <th
                scope="col"
                className="pci-selected-modules-modal__prod-head"
                aria-label="Production (EnFin)"
            >
                <span className="pci-selected-modules-modal__prod-head-label">Production</span>
                <span className="pci-selected-modules-modal__prod-head-vendor">(EnFin)</span>
            </th>
            <th
                scope="col"
                className="pci-selected-modules-modal__prod-head"
                aria-label="Running total (EnFin)"
            >
                <span className="pci-selected-modules-modal__prod-head-label">Running total</span>
                <span className="pci-selected-modules-modal__prod-head-vendor">(EnFin)</span>
            </th>
            <th
                scope="col"
                className="pci-selected-modules-modal__prod-head"
                aria-label="Running module price (EnFin)"
            >
                <span className="pci-selected-modules-modal__prod-head-label">Running price</span>
                <span className="pci-selected-modules-modal__prod-head-vendor">(EnFin)</span>
            </th>
        </>
    );
}

function VendorProductionCells({
    sourceKwh,
    sourceVendor,
    estimated,
    runningOneDrawKwh,
    runningEnfinKwh,
    runningOneDrawRevenue,
    runningEnfinRevenue,
}: {
    sourceKwh: number | null | undefined;
    sourceVendor: ActiveLossesVendor;
    estimated?: boolean;
    runningOneDrawKwh: number | null;
    runningEnfinKwh: number | null;
    runningOneDrawRevenue: number | null;
    runningEnfinRevenue: number | null;
}) {
    const oneDraw = productionForVendor(sourceKwh, sourceVendor, 'oneDRAW');
    const enfin = productionForVendor(sourceKwh, sourceVendor, 'EnFin');
    return (
        <>
            <td
                className="pci-selected-modules-modal__prod pci-selected-modules-modal__prod--onedraw"
                title={
                    estimated
                        ? 'Estimated — the viewer has not published production for this module yet'
                        : 'Annual production with oneDRAW losses'
                }
            >
                {formatVendorProductionKwh(oneDraw, estimated)}
            </td>
            <td
                className="pci-selected-modules-modal__run-total pci-selected-modules-modal__run-total--onedraw"
                title="Cumulative oneDRAW production in this list order"
            >
                {formatModuleProductionKwh(runningOneDrawKwh)}
            </td>
            <td
                className="pci-selected-modules-modal__run-price pci-selected-modules-modal__run-price--onedraw"
                title="Running price: LightReach module PPW × system module wattage"
            >
                {formatRunningModulePrice(runningOneDrawRevenue)}
            </td>
            <td
                className="pci-selected-modules-modal__prod pci-selected-modules-modal__prod--enfin"
                title={
                    estimated
                        ? 'Estimated — the viewer has not published production for this module yet'
                        : 'Annual production with EnFin losses'
                }
            >
                {formatVendorProductionKwh(enfin, estimated)}
            </td>
            <td
                className="pci-selected-modules-modal__run-total pci-selected-modules-modal__run-total--enfin"
                title="Cumulative EnFin production in this list order"
            >
                {formatModuleProductionKwh(runningEnfinKwh)}
            </td>
            <td
                className="pci-selected-modules-modal__run-price pci-selected-modules-modal__run-price--enfin"
                title="Running price: EnFin module PPW × system module wattage"
            >
                {formatRunningModulePrice(runningEnfinRevenue)}
            </td>
        </>
    );
}

export const PreContractSelectedModulesPopover: React.FC<
    PreContractSelectedModulesPopoverProps
> = ({
    layouts,
    allModulesLayouts,
    roofSelections,
    targetKwh = 0,
    escalator = null,
    rate = null,
    form = null,
    systemPpw = null,
    oneDrawSystemPpw = null,
    enfinSystemPpw = null,
    actualProductionKwh,
    estimateMissingProduction = false,
    open,
    onOpenChange,
    contextLabel,
    variant = 'toolbar',
    compact = false,
    disabled = false,
}) => {
    const dialogId = useId();
    const [activeTab, setActiveTab] = useState<ModulesModalTab>('selected');
    const selectedLossesVendor = useViewerStore((state) => state.selectedLossesVendor);
    const productionLossesVendor = useViewerStore((state) => state.pciProductionLossesVendor);
    const systemDesignLosses = useOneButtonStore((state) => state.systemDesignLosses);
    const sourceVendor: ActiveLossesVendor = productionLossesVendor
        ? normalizeLossesVendor(productionLossesVendor)
        : resolveActiveLossesVendor(selectedLossesVendor, systemDesignLosses);
    const rawSummary = summarizeSelectedModules(layouts, roofSelections);
    const productionFallback = useMemo(
        () =>
            estimateMissingProduction
                ? buildModuleProductionFallback({
                      layouts,
                      roofSelections: roofSelections ?? layouts.map(() => true),
                      moduleWattage: form?.moduleTypeWattage ?? null,
                  })
                : null,
        [estimateMissingProduction, form?.moduleTypeWattage, layouts, roofSelections],
    );
    const allModulesOptions = useMemo(
        () => ({
            ...ALL_MODULES_OPTIONS,
            baselineLayouts: allModulesLayouts,
        }),
        [allModulesLayouts],
    );
    const rawAllSummary = summarizeAllSystemModules(layouts, roofSelections, allModulesOptions);
    const selectedRows = useMemo<PricedModuleRow[]>(
        () => fillModuleRows(collectSelectedModules(layouts, roofSelections), productionFallback),
        [layouts, roofSelections, productionFallback],
    );
    const allRows = useMemo(
        () => collectAllSystemModules(layouts, roofSelections, allModulesOptions),
        [layouts, roofSelections, allModulesOptions],
    );
    const sourceTotalKwh =
        sumRowProduction(selectedRows) ?? rawSummary.totalProductionKwh;
    const alignedSourceTotalKwh =
        sourceTotalKwh != null &&
        actualProductionKwh != null &&
        Math.abs(sourceTotalKwh - actualProductionKwh) > 1
            ? actualProductionKwh
            : sourceTotalKwh;
    const oneDrawTotalKwh = productionForVendor(alignedSourceTotalKwh, sourceVendor, 'oneDRAW');
    const enfinTotalKwh = productionForVendor(alignedSourceTotalKwh, sourceVendor, 'EnFin');
    const allOneDrawTotalKwh = productionForVendor(
        rawAllSummary.totalProductionKwh,
        sourceVendor,
        'oneDRAW',
    );
    const allEnfinTotalKwh = productionForVendor(
        rawAllSummary.totalProductionKwh,
        sourceVendor,
        'EnFin',
    );
    const summary = {
        ...rawSummary,
        totalProductionKwh: alignedSourceTotalKwh,
    };
    const hasAnyModules = allRows.length > 0;

    const storeOpportunityId = useOneButtonStore((s: OneButtonState) => s.opportunityID);
    const opportunityDetails = useOneButtonStore((s: OneButtonState) => s.opportunityDetails);
    const opportunityId = useMemo(
        () => resolveStoreOpportunityId() ?? storeOpportunityId ?? null,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [storeOpportunityId, opportunityDetails],
    );
    const [ppwDataset, setPpwDataset] = useState<PpwDetailsDataset | null>(null);

    const selectedEscalator = resolvePreContractEscalator(escalator ?? form?.targetEscalator);
    const selectedRate =
        parsePpaRateValue(rate ?? form?.targetMinPpaRate) || parsePpaRateValue(form?.utilityRate);
    const pricingState = String(form?.installState || '').trim().toUpperCase();

    useEffect(() => {
        if (!open) return undefined;
        let cancelled = false;
        void fetchPpwDetails({
            opportunityId,
            stateCode: pricingState,
            utilityId: form?.utilityCompanyId,
        }).then((next) => {
            if (cancelled) return;
            setPpwDataset(next.status === 'ready' ? next.dataset : null);
        });
        return () => {
            cancelled = true;
        };
    }, [open, opportunityId, pricingState, form?.utilityCompanyId]);

    const partnerPpwByMultiplier = useMemo(() => {
        const lookup = new Map<string, ModulePartnerPpw>();
        if (!(selectedRate > 0) || !pricingState) return lookup;
        const multipliers = new Set<string>();
        selectedRows.forEach((row) => {
            if (row.multiplier != null && row.multiplier > 0) {
                multipliers.add(row.multiplier.toFixed(3));
            }
        });
        allRows.forEach((row) => {
            if (row.multiplier != null && row.multiplier > 0) {
                multipliers.add(row.multiplier.toFixed(3));
            }
        });
        multipliers.forEach((key) => {
            const multiplier = Number(key);
            lookup.set(key, {
                lightReach: quoteModulePartnerPpw({
                    partner: 'LightReach',
                    state: pricingState,
                    rate: selectedRate,
                    escalator: selectedEscalator,
                    multiplier,
                    dataset: ppwDataset,
                }),
                enfin: quoteModulePartnerPpw({
                    partner: 'Enfin',
                    state: pricingState,
                    rate: selectedRate,
                    escalator: selectedEscalator,
                    multiplier,
                    dataset: ppwDataset,
                }),
            });
        });
        return lookup;
    }, [allRows, ppwDataset, pricingState, selectedEscalator, selectedRate, selectedRows]);

    const resolvedSystemPpw = useMemo(() => {
        if (systemPpw != null && Number.isFinite(systemPpw) && systemPpw > 0) return systemPpw;
        const multiplier = summary.systemMultiplier;
        if (multiplier == null || !(multiplier > 0) || !(selectedRate > 0) || !pricingState) {
            return null;
        }
        const quoteInput = {
            state: pricingState,
            rate: selectedRate,
            escalator: selectedEscalator,
            multiplier,
            dataset: ppwDataset,
        };
        return (
            quoteModulePartnerPpw({ ...quoteInput, partner: 'Enfin' }) ??
            quoteModulePartnerPpw({ ...quoteInput, partner: 'LightReach' })
        );
    }, [
        ppwDataset,
        pricingState,
        selectedEscalator,
        selectedRate,
        summary.systemMultiplier,
        systemPpw,
    ]);

    const resolvedOneDrawPpw =
        oneDrawSystemPpw != null && Number.isFinite(oneDrawSystemPpw) && oneDrawSystemPpw > 0
            ? oneDrawSystemPpw
            : resolvedSystemPpw;
    const resolvedEnfinPpw =
        enfinSystemPpw != null && Number.isFinite(enfinSystemPpw) && enfinSystemPpw > 0
            ? enfinSystemPpw
            : resolvedSystemPpw;
    const moduleWattage =
        form?.moduleTypeWattage != null && form.moduleTypeWattage > 0
            ? form.moduleTypeWattage
            : null;

    useEffect(() => {
        if (!open) {
            setActiveTab('selected');
            return undefined;
        }
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onOpenChange(false);
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, onOpenChange]);

    const systemPpwSummary = (
        <span className="pci-selected-modules-modal__summary-item">
            <span className="pci-selected-modules-modal__summary-label">System PPW</span>
            <strong className="pci-selected-modules-modal__summary-value pci-selected-modules-modal__summary-value--pricing">
                {formatSystemPpw(resolvedSystemPpw)}
            </strong>
        </span>
    );
    const buttonLabel =
        summary.count > 0
            ? variant === 'card'
                ? `${summary.count} modules`
                : compact
                  ? `${summary.count} sel`
                  : `Selected modules (${summary.count})`
            : compact
              ? 'Selected'
              : 'Selected modules';

    return (
        <>
            <button
                type="button"
                className={`pci-selected-modules-btn pci-selected-modules-btn--${variant}${
                    compact ? ' pci-selected-modules-btn--compact' : ''
                }${open ? ' pci-selected-modules-btn--open' : ''}`}
                aria-expanded={open}
                aria-controls={dialogId}
                disabled={disabled || !hasAnyModules}
                title={
                    compact
                        ? 'View selected modules'
                        : summary.systemMultiplier != null
                          ? `System multiplier ${summary.systemMultiplier.toFixed(3)}`
                          : 'View selected modules'
                }
                onClick={(event) => {
                    event.stopPropagation();
                    onOpenChange(true);
                }}
            >
                <ListBullets size={variant === 'card' ? 13 : 15} weight="bold" aria-hidden />
                <span>{buttonLabel}</span>
                {summary.systemMultiplier != null && !compact ? (
                    <em className="pci-selected-modules-btn__mult">
                        {summary.systemMultiplier.toFixed(3)}
                    </em>
                ) : null}
            </button>
            {open
                ? createPortal(
                      <div
                          className="pci-selected-modules-modal"
                          role="dialog"
                          aria-modal="true"
                          aria-labelledby={`${dialogId}-title`}
                      >
                          <button
                              type="button"
                              className="pci-selected-modules-modal__backdrop"
                              aria-label="Close selected modules"
                              onClick={() => onOpenChange(false)}
                          />
                          <div id={dialogId} className="pci-selected-modules-modal__dialog">
                              <header className="pci-selected-modules-modal__head">
                                  <div>
                                      <h2 id={`${dialogId}-title`}>Selected modules</h2>
                                      {contextLabel ? (
                                          <p className="pci-selected-modules-modal__context">
                                              {contextLabel}
                                          </p>
                                      ) : null}
                                  </div>
                                  <button
                                      type="button"
                                      className="pci-selected-modules-modal__close"
                                      aria-label="Close"
                                      onClick={() => onOpenChange(false)}
                                  >
                                      <X size={18} weight="bold" />
                                  </button>
                              </header>
                              <div className="pci-selected-modules-modal__summary">
                                  {activeTab === 'selected' ? (
                                      <>
                                          <span className="pci-selected-modules-modal__summary-item">
                                              <span className="pci-selected-modules-modal__summary-label">
                                                  Modules
                                              </span>
                                              <strong className="pci-selected-modules-modal__summary-value">
                                                  {summary.count}
                                              </strong>
                                          </span>
                                          {summary.systemMultiplier != null ? (
                                              <span className="pci-selected-modules-modal__summary-item">
                                                  <span className="pci-selected-modules-modal__summary-label">
                                                      System mult
                                                  </span>
                                                  <strong className="pci-selected-modules-modal__summary-value">
                                                      {summary.systemMultiplier.toFixed(3)}
                                                  </strong>
                                              </span>
                                          ) : null}
                                          {oneDrawTotalKwh != null ? (
                                              <span className="pci-selected-modules-modal__summary-item">
                                                  <span className="pci-selected-modules-modal__summary-label">
                                                      oneDRAW
                                                  </span>
                                                  <strong className="pci-selected-modules-modal__summary-value pci-selected-modules-modal__summary-value--production">
                                                      {formatModuleProductionKwh(oneDrawTotalKwh)}
                                                  </strong>
                                              </span>
                                          ) : null}
                                          {enfinTotalKwh != null ? (
                                              <span className="pci-selected-modules-modal__summary-item">
                                                  <span className="pci-selected-modules-modal__summary-label">
                                                      EnFin
                                                  </span>
                                                  <strong className="pci-selected-modules-modal__summary-value pci-selected-modules-modal__summary-value--production">
                                                      {formatModuleProductionKwh(enfinTotalKwh)}
                                                  </strong>
                                              </span>
                                          ) : null}
                                      </>
                                  ) : (
                                      <>
                                          <span className="pci-selected-modules-modal__summary-item">
                                              <span className="pci-selected-modules-modal__summary-label">
                                                  All modules
                                              </span>
                                              <strong className="pci-selected-modules-modal__summary-value">
                                                  {rawAllSummary.count}
                                              </strong>
                                          </span>
                                          <span className="pci-selected-modules-modal__summary-item">
                                              <span className="pci-selected-modules-modal__summary-label">
                                                  Selected
                                              </span>
                                              <strong className="pci-selected-modules-modal__summary-value">
                                                  {rawAllSummary.selectedCount}
                                              </strong>
                                          </span>
                                          {allOneDrawTotalKwh != null ? (
                                              <span className="pci-selected-modules-modal__summary-item">
                                                  <span className="pci-selected-modules-modal__summary-label">
                                                      oneDRAW
                                                  </span>
                                                  <strong className="pci-selected-modules-modal__summary-value pci-selected-modules-modal__summary-value--production">
                                                      {formatModuleProductionKwh(allOneDrawTotalKwh)}
                                                  </strong>
                                              </span>
                                          ) : null}
                                          {allEnfinTotalKwh != null ? (
                                              <span className="pci-selected-modules-modal__summary-item">
                                                  <span className="pci-selected-modules-modal__summary-label">
                                                      EnFin
                                                  </span>
                                                  <strong className="pci-selected-modules-modal__summary-value pci-selected-modules-modal__summary-value--production">
                                                      {formatModuleProductionKwh(allEnfinTotalKwh)}
                                                  </strong>
                                              </span>
                                          ) : null}
                                      </>
                                  )}
                                  {systemPpwSummary}
                                  <span className="pci-selected-modules-modal__summary-item">
                                      <span className="pci-selected-modules-modal__summary-label">
                                          Escalator
                                      </span>
                                      <strong className="pci-selected-modules-modal__summary-value pci-selected-modules-modal__summary-value--pricing">
                                          {formatSandboxEscalator(
                                              resolvePreContractEscalator(escalator),
                                          )}
                                      </strong>
                                  </span>
                                  <span className="pci-selected-modules-modal__summary-item">
                                      <span className="pci-selected-modules-modal__summary-label">
                                          Rate
                                      </span>
                                      <strong className="pci-selected-modules-modal__summary-value pci-selected-modules-modal__summary-value--pricing">
                                          {formatSummaryRate(rate)}
                                      </strong>
                                  </span>
                                  {targetKwh > 0 ? (
                                      <span className="pci-selected-modules-modal__summary-item">
                                          <span className="pci-selected-modules-modal__summary-label">
                                              Target
                                          </span>
                                          <strong className="pci-selected-modules-modal__summary-value pci-selected-modules-modal__summary-value--target">
                                              {formatModuleProductionKwh(targetKwh)}
                                          </strong>
                                      </span>
                                  ) : null}
                                  <button
                                      type="button"
                                      className="pci-selected-modules-modal__export"
                                      onClick={() =>
                                          exportPreContractModulesWorkbook({
                                              contextLabel,
                                              selectedRows,
                                              allRows,
                                              targetKwh,
                                              sourceVendor,
                                              partnerPpwByMultiplier,
                                              moduleWattage,
                                              systemPpw: resolvedSystemPpw,
                                              selectedRate,
                                              escalator: resolvePreContractEscalator(escalator),
                                              oneDrawTotalKwh,
                                              enfinTotalKwh,
                                              selectedCount: summary.count,
                                              systemMultiplier: summary.systemMultiplier,
                                              allCount: rawAllSummary.count,
                                              allSelectedCount: rawAllSummary.selectedCount,
                                          })
                                      }
                                  >
                                      <MicrosoftExcelLogo size={15} weight="fill" aria-hidden />
                                      Export Excel
                                  </button>
                              </div>
                              <div
                                  className="pci-selected-modules-modal__tabs"
                                  role="tablist"
                                  aria-label="Module list views"
                              >
                                  <button
                                      type="button"
                                      role="tab"
                                      className={`pci-selected-modules-modal__tab${
                                          activeTab === 'selected'
                                              ? ' pci-selected-modules-modal__tab--on'
                                              : ''
                                      }`}
                                      aria-selected={activeTab === 'selected'}
                                      onClick={() => setActiveTab('selected')}
                                  >
                                      Selected ({summary.count})
                                  </button>
                                  <button
                                      type="button"
                                      role="tab"
                                      className={`pci-selected-modules-modal__tab${
                                          activeTab === 'all' ? ' pci-selected-modules-modal__tab--on' : ''
                                      }`}
                                      aria-selected={activeTab === 'all'}
                                      onClick={() => setActiveTab('all')}
                                  >
                                      All modules ({rawAllSummary.count})
                                  </button>
                              </div>
                              {activeTab === 'selected' ? (
                                  <SelectedModulesTable
                                      rows={selectedRows}
                                      targetKwh={targetKwh}
                                      sourceVendor={sourceVendor}
                                      partnerPpwByMultiplier={partnerPpwByMultiplier}
                                      moduleWattage={moduleWattage}
                                  />
                              ) : (
                                  <AllModulesTable
                                      rows={allRows}
                                      targetKwh={targetKwh}
                                      sourceVendor={sourceVendor}
                                      partnerPpwByMultiplier={partnerPpwByMultiplier}
                                      moduleWattage={moduleWattage}
                                  />
                              )}
                          </div>
                      </div>,
                      document.body,
                  )
                : null}
        </>
    );
};

type RowWithRunningTotal = PricedModuleRow & {
    runningOneDrawKwh: number | null;
    runningEnfinKwh: number | null;
    runningOneDrawRevenue: number | null;
    runningEnfinRevenue: number | null;
    pastTarget: boolean;
};

function buildRowsWithRunningTotal(
    rows: PricedModuleRow[],
    targetKwh: number,
    sourceVendor: ActiveLossesVendor,
    partnerPpwByMultiplier: Map<string, ModulePartnerPpw>,
    moduleWattage: number | null,
): RowWithRunningTotal[] {
    let runningOneDrawKwh = 0;
    let runningEnfinKwh = 0;
    let runningOneDrawRevenue = 0;
    let runningEnfinRevenue = 0;
    return rows.map((row) => {
        const prevTotal = runningOneDrawKwh;
        const oneDraw = productionForVendor(row.productionKwh, sourceVendor, 'oneDRAW');
        const enfin = productionForVendor(row.productionKwh, sourceVendor, 'EnFin');
        const partnerPpw = partnerPpwForMultiplier(row.multiplier, partnerPpwByMultiplier);
        if (oneDraw != null) runningOneDrawKwh += oneDraw;
        if (enfin != null) runningEnfinKwh += enfin;
        const oneDrawRev = moduleWattagePrice(partnerPpw.lightReach, moduleWattage);
        const enfinRev = moduleWattagePrice(partnerPpw.enfin, moduleWattage);
        if (oneDrawRev != null) runningOneDrawRevenue += oneDrawRev;
        if (enfinRev != null) runningEnfinRevenue += enfinRev;
        return {
            ...row,
            runningOneDrawKwh: runningOneDrawKwh > 0 ? Math.round(runningOneDrawKwh) : null,
            runningEnfinKwh: runningEnfinKwh > 0 ? Math.round(runningEnfinKwh) : null,
            runningOneDrawRevenue: runningOneDrawRevenue > 0 ? runningOneDrawRevenue : null,
            runningEnfinRevenue: runningEnfinRevenue > 0 ? runningEnfinRevenue : null,
            pastTarget: targetKwh > 0 && prevTotal >= targetKwh,
        };
    });
}

type AllModuleRowWithTotals = SystemModuleRow & {
    runningOneDrawKwh: number | null;
    runningEnfinKwh: number | null;
    runningOneDrawRevenue: number | null;
    runningEnfinRevenue: number | null;
    activeRunningTotalKwh: number | null;
    pastTarget: boolean;
    pastActiveTarget: boolean;
};

function buildAllModuleRowsWithTotals(
    rows: SystemModuleRow[],
    targetKwh: number,
    sourceVendor: ActiveLossesVendor,
    partnerPpwByMultiplier: Map<string, ModulePartnerPpw>,
    moduleWattage: number | null,
): AllModuleRowWithTotals[] {
    let runningOneDrawKwh = 0;
    let runningEnfinKwh = 0;
    let runningOneDrawRevenue = 0;
    let runningEnfinRevenue = 0;
    let activeRunningTotalKwh = 0;

    return rows.map((row) => {
        const prevTotal = runningOneDrawKwh;
        const prevActiveTotal = activeRunningTotalKwh;
        const oneDraw = productionForVendor(row.productionKwh, sourceVendor, 'oneDRAW');
        const enfin = productionForVendor(row.productionKwh, sourceVendor, 'EnFin');
        const partnerPpw = partnerPpwForMultiplier(row.multiplier, partnerPpwByMultiplier);

        if (oneDraw != null) runningOneDrawKwh += oneDraw;
        if (enfin != null) runningEnfinKwh += enfin;
        if (row.included) {
            const oneDrawRev = moduleWattagePrice(partnerPpw.lightReach, moduleWattage);
            const enfinRev = moduleWattagePrice(partnerPpw.enfin, moduleWattage);
            if (oneDrawRev != null) runningOneDrawRevenue += oneDrawRev;
            if (enfinRev != null) runningEnfinRevenue += enfinRev;
        }
        if (row.included && oneDraw != null) {
            activeRunningTotalKwh += oneDraw;
        }

        return {
            ...row,
            runningOneDrawKwh: runningOneDrawKwh > 0 ? Math.round(runningOneDrawKwh) : null,
            runningEnfinKwh: runningEnfinKwh > 0 ? Math.round(runningEnfinKwh) : null,
            runningOneDrawRevenue: runningOneDrawRevenue > 0 ? runningOneDrawRevenue : null,
            runningEnfinRevenue: runningEnfinRevenue > 0 ? runningEnfinRevenue : null,
            activeRunningTotalKwh:
                row.included && activeRunningTotalKwh > 0
                    ? Math.round(activeRunningTotalKwh)
                    : null,
            pastTarget: targetKwh > 0 && prevTotal >= targetKwh,
            pastActiveTarget: targetKwh > 0 && row.included && prevActiveTotal >= targetKwh,
        };
    });
}

function AllModulesTable({
    rows,
    targetKwh,
    sourceVendor,
    partnerPpwByMultiplier,
    moduleWattage,
}: {
    rows: SystemModuleRow[];
    targetKwh: number;
    sourceVendor: ActiveLossesVendor;
    partnerPpwByMultiplier: Map<string, ModulePartnerPpw>;
    moduleWattage: number | null;
}) {
    if (rows.length === 0) {
        return <p className="pci-selected-modules-modal__empty">No modules on this system.</p>;
    }
    const rowsWithTotals = buildAllModuleRowsWithTotals(
        rows,
        targetKwh,
        sourceVendor,
        partnerPpwByMultiplier,
        moduleWattage,
    );
    return (
        <div className="pci-selected-modules-modal__body">
            <table className="pci-selected-modules-modal__table pci-selected-modules-modal__table--all">
                <thead>
                    <tr>
                        <th scope="col">Module</th>
                        <th scope="col" aria-label="Selected">
                            Sel
                        </th>
                        <th scope="col">Mult</th>
                        <VendorProductionHeaders />
                        <th scope="col">Active total</th>
                        <th scope="col">SA</th>
                        {SHOW_MODULE_PARTNER_PPW_COLUMNS ? <PartnerPpwHeaders /> : null}
                    </tr>
                </thead>
                <tbody>
                    {rowsWithTotals.map((row, index) => (
                        <tr
                            key={`${row.roofLabel}:${row.id}:${index}`}
                            className={[
                                row.pastTarget || row.pastActiveTarget
                                    ? 'pci-selected-modules-modal__row--past-target'
                                    : '',
                                !row.included ? ' pci-selected-modules-modal__row--excluded' : '',
                                row.shadeTier === 'grey'
                                    ? ' pci-selected-modules-modal__row--grey'
                                    : '',
                                row.shadeTier === 'purple'
                                    ? ' pci-selected-modules-modal__row--purple'
                                    : '',
                            ]
                                .join('')
                                .trim()}
                        >
                            <ModuleIdentityCell
                                id={row.id}
                                roofLabel={row.roofLabel}
                                wattage={moduleWattage}
                            />
                            <td aria-label={row.included ? 'Selected' : 'Not selected'}>
                                {row.included ? (
                                    <Check
                                        size={14}
                                        weight="bold"
                                        className="pci-selected-modules-modal__sel-icon"
                                        aria-hidden
                                    />
                                ) : (
                                    '—'
                                )}
                            </td>
                            <td>{row.multiplier != null ? row.multiplier.toFixed(3) : '—'}</td>
                            <VendorProductionCells
                                sourceKwh={row.productionKwh}
                                sourceVendor={sourceVendor}
                                runningOneDrawKwh={row.runningOneDrawKwh}
                                runningEnfinKwh={row.runningEnfinKwh}
                                runningOneDrawRevenue={row.runningOneDrawRevenue}
                                runningEnfinRevenue={row.runningEnfinRevenue}
                            />
                            <td>
                                {row.included
                                    ? formatModuleProductionKwh(row.activeRunningTotalKwh)
                                    : '—'}
                            </td>
                            <td>{row.solarAccess != null ? `${row.solarAccess.toFixed(1)}%` : '—'}</td>
                            {SHOW_MODULE_PARTNER_PPW_COLUMNS ? (
                                <PartnerPpwCells
                                    multiplier={row.multiplier}
                                    lookup={partnerPpwByMultiplier}
                                />
                            ) : null}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function SelectedModulesTable({
    rows,
    targetKwh,
    sourceVendor,
    partnerPpwByMultiplier,
    moduleWattage,
}: {
    rows: PricedModuleRow[];
    targetKwh: number;
    sourceVendor: ActiveLossesVendor;
    partnerPpwByMultiplier: Map<string, ModulePartnerPpw>;
    moduleWattage: number | null;
}) {
    if (rows.length === 0) {
        return (
            <p className="pci-selected-modules-modal__empty">No modules are selected.</p>
        );
    }
    const rowsWithTotals = buildRowsWithRunningTotal(
        rows,
        targetKwh,
        sourceVendor,
        partnerPpwByMultiplier,
        moduleWattage,
    );
    return (
        <div className="pci-selected-modules-modal__body">
            <table className="pci-selected-modules-modal__table">
                <thead>
                    <tr>
                        <th scope="col">Module</th>
                        <th scope="col">Mult</th>
                        <VendorProductionHeaders />
                        <th scope="col">SA</th>
                        {SHOW_MODULE_PARTNER_PPW_COLUMNS ? <PartnerPpwHeaders /> : null}
                    </tr>
                </thead>
                <tbody>
                    {rowsWithTotals.map((row) => (
                        <tr
                            key={row.id}
                            className={
                                row.pastTarget ? ' pci-selected-modules-modal__row--past-target' : ''
                            }
                        >
                            <ModuleIdentityCell
                                id={row.id}
                                roofLabel={row.roofLabel}
                                wattage={moduleWattage}
                            />
                            <td>{row.multiplier != null ? row.multiplier.toFixed(3) : '—'}</td>
                            <VendorProductionCells
                                sourceKwh={row.productionKwh}
                                sourceVendor={sourceVendor}
                                estimated={row.estimated}
                                runningOneDrawKwh={row.runningOneDrawKwh}
                                runningEnfinKwh={row.runningEnfinKwh}
                                runningOneDrawRevenue={row.runningOneDrawRevenue}
                                runningEnfinRevenue={row.runningEnfinRevenue}
                            />
                            <td>{row.solarAccess != null ? `${row.solarAccess.toFixed(1)}%` : '—'}</td>
                            {SHOW_MODULE_PARTNER_PPW_COLUMNS ? (
                                <PartnerPpwCells
                                    multiplier={row.multiplier}
                                    lookup={partnerPpwByMultiplier}
                                />
                            ) : null}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function excelValue(value: number | null | undefined, digits = 3): number | '' {
    if (value == null || !Number.isFinite(value)) return '';
    const factor = 10 ** digits;
    return Math.round(value * factor) / factor;
}

function moduleExportFileName(contextLabel?: string): string {
    const stamp = new Date().toISOString().slice(0, 10);
    const slug = (contextLabel ?? 'modules')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 40);
    return `selected-modules${slug ? `-${slug}` : ''}-${stamp}.xlsx`;
}

function selectedModulesSheetRows(
    rows: PricedModuleRow[],
    sourceVendor: ActiveLossesVendor,
    partnerPpwByMultiplier: Map<string, ModulePartnerPpw>,
    moduleWattage: number | null,
    includeSelectedColumn: boolean,
): (string | number)[][] {
    const withTotals = includeSelectedColumn
        ? buildAllModuleRowsWithTotals(
              rows as SystemModuleRow[],
              0,
              sourceVendor,
              partnerPpwByMultiplier,
              moduleWattage,
          )
        : buildRowsWithRunningTotal(
              rows,
              0,
              sourceVendor,
              partnerPpwByMultiplier,
              moduleWattage,
          );
    const header = [
        'Module',
        'Roof',
        ...(includeSelectedColumn ? ['Selected'] : []),
        'Wattage (W)',
        'Multiplier',
        'Solar access (%)',
        'Production oneDRAW (kWh)',
        'Running total oneDRAW (kWh)',
        'Running price oneDRAW',
        'Production EnFin (kWh)',
        'Running total EnFin (kWh)',
        'Running price EnFin',
        'LightReach PPW',
        'EnFin PPW',
    ];
    const body = withTotals.map((row) => {
        const oneDraw = productionForVendor(row.productionKwh, sourceVendor, 'oneDRAW');
        const enfin = productionForVendor(row.productionKwh, sourceVendor, 'EnFin');
        const ppw = partnerPpwForMultiplier(row.multiplier, partnerPpwByMultiplier);
        const selected =
            includeSelectedColumn && 'included' in row
                ? (row as SystemModuleRow).included
                    ? 'Yes'
                    : 'No'
                : null;
        return [
            row.id,
            row.roofLabel,
            ...(includeSelectedColumn ? [selected ?? ''] : []),
            excelValue(moduleWattage, 0),
            excelValue(row.multiplier, 3),
            excelValue(row.solarAccess, 1),
            excelValue(oneDraw, 0),
            excelValue(row.runningOneDrawKwh, 0),
            excelValue(row.runningOneDrawRevenue, 2),
            excelValue(enfin, 0),
            excelValue(row.runningEnfinKwh, 0),
            excelValue(row.runningEnfinRevenue, 2),
            excelValue(ppw.lightReach, 2),
            excelValue(ppw.enfin, 2),
        ];
    });
    return [header, ...body];
}

function exportPreContractModulesWorkbook(input: {
    contextLabel?: string;
    selectedRows: PricedModuleRow[];
    allRows: SystemModuleRow[];
    targetKwh: number;
    sourceVendor: ActiveLossesVendor;
    partnerPpwByMultiplier: Map<string, ModulePartnerPpw>;
    moduleWattage: number | null;
    systemPpw: number | null;
    selectedRate: number;
    escalator: number;
    oneDrawTotalKwh: number | null;
    enfinTotalKwh: number | null;
    selectedCount: number;
    systemMultiplier: number | null;
    allCount: number;
    allSelectedCount: number;
}) {
    const wb = XLSX.utils.book_new();
    const summary = [
        ['Selected modules export'],
        ['Design', input.contextLabel ?? ''],
        ['Exported', new Date().toISOString()],
        [],
        ['Modules', input.selectedCount],
        ['All modules', input.allCount],
        ['Selected in all list', input.allSelectedCount],
        ['System multiplier', excelValue(input.systemMultiplier, 3)],
        ['System PPW', excelValue(input.systemPpw, 3)],
        ['Module wattage (W)', excelValue(input.moduleWattage, 0)],
        ['Escalator', excelValue(input.escalator, 4)],
        ['Rate ($/kWh)', excelValue(input.selectedRate, 3)],
        ['Target (kWh)', input.targetKwh > 0 ? excelValue(input.targetKwh, 0) : ''],
        ['oneDRAW production (kWh)', excelValue(input.oneDrawTotalKwh, 0)],
        ['EnFin production (kWh)', excelValue(input.enfinTotalKwh, 0)],
    ];
    const summarySheet = XLSX.utils.aoa_to_sheet(summary);
    summarySheet['!cols'] = [{ wch: 28 }, { wch: 36 }];
    XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');

    const selectedSheet = XLSX.utils.aoa_to_sheet(
        selectedModulesSheetRows(
            input.selectedRows,
            input.sourceVendor,
            input.partnerPpwByMultiplier,
            input.moduleWattage,
            false,
        ),
    );
    selectedSheet['!cols'] = Array(14).fill({ wch: 16 });
    XLSX.utils.book_append_sheet(wb, selectedSheet, 'Selected');

    const allSheet = XLSX.utils.aoa_to_sheet(
        selectedModulesSheetRows(
            input.allRows,
            input.sourceVendor,
            input.partnerPpwByMultiplier,
            input.moduleWattage,
            true,
        ),
    );
    allSheet['!cols'] = Array(15).fill({ wch: 16 });
    XLSX.utils.book_append_sheet(wb, allSheet, 'All modules');

    XLSX.writeFile(wb, moduleExportFileName(input.contextLabel));
}

export default PreContractSelectedModulesPopover;
